import isEqual from 'lodash/isEqual';
import { Transcript } from '../../../Transcript';
import type { BaseAssetSettings } from '../../BaseAsset';
import { TextAssetStyle } from '../../TextAsset/TextAssetStyle';
import type { CaptionRendererSettings } from '../renderer/CaptionRenderer';
import { CaptionRenderer } from '../renderer/CaptionRenderer';
import { tallestStringsFromTranscript } from '../../TextAsset/metrics/TextMetrics';
import type { TextStyleRendererSettings } from '../../TextAsset/renderer/TextStyleRenderer';
import { TextStyleRenderer } from '../../TextAsset/renderer/TextStyleRenderer';
import { MAX_WIDTH_ACROSS_ALL_ASPECT_RATIOS } from '../../../../aspectRatioConfig';
import { BaseTextAsset } from '../../BaseTextAsset';
import { CanvasFontSizeToFitAllTextBinarySearch } from '../../TextAsset/metrics/FontSizeToFitAllTextBinarySearch';
import { buildMetricsForCaptions } from '../metrics/CaptionMetrics';
import type { LoadSettings } from '../../../serializableMixin';
import type { PlaybackRegion } from '../../../PlaybackRegions';
import { buildCuesFromCaptions, buildSRTFromCaptions } from '../../captionUtils';
import type { RawAssetData } from '../../../../video-data/types';
import { reaction } from '../../../reactivity';

/**
 * CanvasCaptionAsset integrates our CanvasCaption renderer into Luminary and provides us with a way to render
 * transcript captions.
 */
export class CaptionAsset extends BaseTextAsset<CaptionRenderer> {
    override _classType = 'CanvasCaptionAsset';
    #sceneStartTimes: readonly number[] = [];

    normalStyle: TextAssetStyle;

    /**
     * @deprecated Use normalStyle instead
     */
    get style() {
        return this.normalStyle;
    }

    transcript: Transcript = new Transcript({
        parent: this,
        fetchResources: this.fetchResources,
        autoUpdate: this.autoDraw,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: BaseAssetSettings) {
        const renderer = new CaptionRenderer();
        super(container, settings, renderer);

        const styleSettings = {
            parent: this,
            autoUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
            getReferenceResolution: () => this.getReferenceResolution(),
            getOutputResolution: () => this.getOutputResolution(),
        };
        this.normalStyle = new TextAssetStyle(styleSettings);

        this.addReaction(
            reaction(
                () => this.visible,
                (visible) => {
                    if (visible && this.#builtWhileHidden) {
                        this.#builtWhileHidden = false;
                        this.requiresRedraw = true;
                    }
                },
                { name: 'CaptionAsset.rebuildWhenShown' },
            ),
        );
    }

    #builtWhileHidden = false;

    protected override _build(): void {
        super._build();
        this.#builtWhileHidden = !this.visible;
    }

    override get styles() {
        // We only support one style for the moment, while some assets have normal + hightlight style
        return { normalStyle: this.normalStyle };
    }

    get hasText() {
        return this.transcript._words.length !== 0;
    }

    #fallbackFontTextWords: unknown = null;
    #fallbackFontText = '';

    // The transcript replaces its word list on every change, so join it once per list
    protected override get _fallbackFontText(): string {
        const words = this.transcript._words;
        if (words !== this.#fallbackFontTextWords) {
            this.#fallbackFontTextWords = words;
            this.#fallbackFontText = this.transcript.text;
        }
        return this.#fallbackFontText;
    }

    /** @internal */
    get _sceneStartTimes(): readonly number[] {
        return this.#sceneStartTimes;
    }

    /** @internal */
    set _sceneStartTimes(value: readonly number[]) {
        if (isEqual(this.#sceneStartTimes, value)) {
            return;
        }
        this.#sceneStartTimes = value;
        this.requiresRedraw = true;
    }

    /**
     * Create a bridge for all asset properties into canvas compatible properties. This includes transcript, style and
     * dynamic styling properties. The output is an object that can be passed to the canvas renderer build method.
     * */
    override getRendererSettings(): CaptionRendererSettings {
        this._syncFontSizeAndLeadingToStyles();
        return {
            ...super.getRendererSettings(),
            transcript: this.transcript,
            alignment: this.textAlign,
            normalStyle: this.normalStyle.getRendererSettings() as TextStyleRendererSettings,
            sceneStartTimes: this.#sceneStartTimes,
        };
    }

    override set autoDraw(value: boolean) {
        if (this.autoDraw === value) {
            return;
        }
        super.autoDraw = value;
        // Because the transcript extends from TimeInvariantBase and isn't a BaseAsset,
        // we need to manually connect the two different update loops
        this.transcript.autoUpdate = value;
    }

    override get autoDraw() {
        return super.autoDraw;
    }

    override _setAutoDrawForAllChildren(value: boolean) {
        super._setAutoDrawForAllChildren(value);
        // connect the transcript to the autoDraw update loop
        this.transcript.autoUpdate = value;
    }

    async generateSRTText(
        timeOffset: number,
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
        captionAssetDuration: number,
    ): Promise<string[]> {
        return buildSRTFromCaptions(
            this,
            buildMetricsForCaptions,
            this.renderer.hasMetrics,
            timeOffset,
            playbackRegions,
            captionAssetDuration,
        );
    }

    async generateVTTText(
        timeOffset: number,
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
        captionAssetDuration: number,
    ): Promise<string[]> {
        return buildCuesFromCaptions(
            this,
            buildMetricsForCaptions,
            this.renderer.hasMetrics,
            timeOffset,
            playbackRegions,
            captionAssetDuration,
            'vtt',
        );
    }

    /** Sync the font size and leading to the style object. */
    _syncFontSizeAndLeadingToStyles() {
        this.normalStyle.fontSize = this.textSize;
        this.normalStyle.leading = this.textSize * this.leadingMultiplier;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['normalStyle', 'transcript']);
    }

    override get readonlyProperties() {
        return ['classType'];
    }

    override get editableProperties() {
        return super.editableProperties.concat(['normalStyle']);
    }

    override load(data: RawAssetData, settings?: LoadSettings) {
        this._batchRedraw(() => {
            super.load(data, settings);

            const fontSize = this.textSize;
            const leading = this.textSize * this.leadingMultiplier;
            this.normalStyle.load(
                {
                    ...(data.normalStyle as Record<string, unknown>),
                    fontSize,
                    leading,
                },
                settings,
            );
        });
        return this;
    }

    /** Returns the maximum font size for the given font that can fit on one line. */
    getFontSizeToFitTallestText(fontSizeRange = { min: 50, max: 250 }) {
        const settings = this.getRendererSettings();
        const style = new TextStyleRenderer();
        style.apply(settings.normalStyle);

        const canvasFontSizeRange = {
            min: Math.floor(fontSizeRange.min * this.referenceToOutputMultiplier),
            max: Math.floor(fontSizeRange.max * this.referenceToOutputMultiplier),
        };

        // get the tallest text that can fit on one line
        let tallestText = '';
        if (this.renderer.hasChunks) {
            tallestText = this.renderer.getTallestText();
        } else {
            tallestText = tallestStringsFromTranscript(this.transcript, style).join('');
        }

        // it's possible the transcript is empty (has no text)
        if (tallestText === '') {
            return {
                fontSize: this.normalStyle.fontSize,
                displaysAllText: true,
                tallestText: '',
            };
        }

        const { fontSize: canvasFontSize, displaysAllText } =
            CanvasFontSizeToFitAllTextBinarySearch.search({
                text: tallestText,
                alignment: settings.alignment,
                normalStyle: style,
                highlightStyle: style,
                highlightAreas: [],
                // since we're only after the tallest text that can fit on one line we don't need to worry about respecting
                // the current maxWidth which is why we're using a big number here. Also note that this is a canvas value,
                // not a luminary base value
                wordBreak: true,
                maxWidth: MAX_WIDTH_ACROSS_ALL_ASPECT_RATIOS,
                maxHeight: settings.maxTextHeight,
                fontSizeRange: canvasFontSizeRange,
            });

        const assetFontSize = Math.floor(canvasFontSize / this.referenceToOutputMultiplier);

        return {
            fontSize: assetFontSize,
            displaysAllText,
            tallestText,
        };
    }
}
